/**
 * ResponsiveTable — the shared admin table primitive (DESIGN.md "Wide admin
 * tables").
 *
 * One DOM tree, two presentations: a real <table> at and above the breakpoint,
 * a stack of cards below it. Nothing is duplicated or conditionally rendered —
 * only `display` changes — so row state, focus, and tests are unaffected by
 * viewport.
 *
 * Carried across from the RMS repo whole, the way `shared/` and DESIGN.md
 * were at the fork, and free to drift from there: it is one product's
 * table, and intake's first destination should not invent a second
 * answer to what a row looks like on a phone (#85).
 *
 * Two call-site decisions the primitive deliberately does NOT make for you:
 *
 *   scroll="cards"  — below the breakpoint each row becomes a bordered card.
 *                     The only safe choice for a table containing an
 *                     absolutely-positioned dropdown: `overflow-x: auto`
 *                     implies `overflow-y: auto`, which clips a `top-full`
 *                     menu on the bottom rows.
 *   scroll="x-auto" — the wide grid scrolls horizontally at and above the
 *                     breakpoint instead: the table keeps its min width and the
 *                     wrapper div scrolls. Permitted ONLY where no cell contains
 *                     an absolutely-positioned dropdown (e.g. the events list,
 *                     whose rows are plain links).
 *
 * Geometry — padding, column width, the room a corner control needs — is the
 * primitive's and is named from a closed set (#443). It used to be a Tailwind
 * class string the call site passed, with a comment explaining that it
 * replaced the default rather than merging, because the repo has no
 * tailwind-merge and the winner would otherwise be CSS source order. A closed
 * set removes the question: there is nothing to merge, and a table cannot be
 * given a padding the row beside it disagrees with.
 */
import {
  createContext,
  useContext,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import { cx } from "@shared/cx";
import styles from "./ResponsiveTable.module.css";

export type TableBreakpoint = "sm" | "md";

/** Cell padding. See the file header for why this is a closed set. */
export type TablePadding =
  | "default"
  | "tight"
  | "header"
  | "compact"
  | "group"
  | "none"
  | "list"
  /** No padding below the breakpoint; the list padding above it. */
  | "listAbove";

/** Fixed column widths, for numeric columns that must not reflow. */
export type TableWidth = "8" | "10" | "14" | "20" | "24" | "28" | "36" | "10Above";

/**
 * Room reserved on the mobile card for the row's `corner` control. Applies
 * below the breakpoint only — above it the corner is an ordinary cell.
 */
export type TableCornerGap = "sm" | "md" | "lg";

const BREAKPOINT = {
  sm: styles.breakSm,
  md: styles.breakMd,
} as const;

const PADDING: Record<TablePadding, string | undefined> = {
  default: styles.padDefault,
  tight: styles.padTight,
  header: styles.padHeader,
  compact: styles.padCompact,
  group: styles.padGroup,
  none: styles.padNone,
  list: styles.padList,
  listAbove: styles.padListMd,
};

const WIDTH: Record<TableWidth, string | undefined> = {
  "8": styles.w8,
  "10": styles.w10,
  "14": styles.w14,
  "20": styles.w20,
  "24": styles.w24,
  "28": styles.w28,
  "36": styles.w36,
  "10Above": styles.w10Md,
};

const CORNER_GAP: Record<TableCornerGap, string | undefined> = {
  sm: styles.cornerGapSm,
  md: styles.cornerGapMd,
  lg: styles.cornerGapLg,
};

/** Mobile card grid width, and the spans a cell may claim within it. */
const CARD_COLUMNS = {
  1: styles.columns1,
  2: styles.columns2,
  4: styles.columns4,
} as const;

const CARD_SPANS = {
  1: styles.span1,
  2: styles.span2,
  4: styles.span4,
} as const;

export type CardColumns = keyof typeof CARD_COLUMNS;
export type CardSpan = keyof typeof CARD_SPANS;

const ColumnsContext = createContext<CardColumns>(4);

interface ResponsiveTableProps {
  children: ReactNode;
  /** Width at which the card stack reverts to a real table. Default `sm`. */
  breakpoint?: TableBreakpoint;
  /** See the file header — `x-auto` is unsafe with in-cell dropdowns. */
  scroll?: "cards" | "x-auto";
  className?: string;
}

export function ResponsiveTable({
  children,
  breakpoint = "sm",
  scroll = "cards",
  className,
}: ResponsiveTableProps) {
  const scrollTable =
    breakpoint === "sm" ? styles.scrollTableSm : styles.scrollTableMd;
  const scrollWrap =
    breakpoint === "sm" ? styles.scrollWrapSm : styles.scrollWrapMd;
  const table = (
    <table
      className={cx(
        styles.table,
        BREAKPOINT[breakpoint],
        scroll === "x-auto" && scrollTable,
        className,
      )}
    >
      {children}
    </table>
  );
  return scroll === "x-auto" ? (
    <div className={scrollWrap}>{table}</div>
  ) : (
    table
  );
}

/** Column headings. Hidden below the breakpoint — cells carry `label` instead. */
export function TableHead({ children }: { children: ReactNode }) {
  return (
    <thead className={styles.head}>
      <tr className={styles.headRow}>{children}</tr>
    </thead>
  );
}

interface TableHeaderCellProps
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, "className"> {
  align?: "left" | "right";
  padding?: TablePadding;
  width?: TableWidth;
  className?: string;
}

export function TableHeaderCell({
  align = "left",
  padding = "default",
  width,
  className,
  children,
  ...rest
}: TableHeaderCellProps) {
  return (
    <th
      className={cx(
        styles.headerCell,
        PADDING[padding],
        width && WIDTH[width],
        align === "right" && styles.alignRight,
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TableBody({ children }: { children: ReactNode }) {
  return <tbody className={styles.body}>{children}</tbody>;
}

/**
 * Footer rows (totals, counts). Unlike the <thead>, the footer carries
 * information rather than column names, so it stays visible below the
 * breakpoint — its rows reflow as blocks like everything else.
 */
export function TableFoot({ children }: { children: ReactNode }) {
  return <tfoot className={styles.foot}>{children}</tfoot>;
}

interface TableRowProps {
  children: ReactNode;
  /**
   * `card` (default) — a bordered card below the breakpoint, for data rows.
   * `plain` — no card treatment, for group headings, subtotals, and full-width
   * notices that read as dividers rather than records.
   */
  variant?: "card" | "plain";
  /**
   * Mobile card grid width. `4` (default) suits the quote/gear shape: a
   * full-width name row over paired numeric fields. `1` stacks every field
   * label-above-value — the right choice for many narrow inputs (job prep's
   * ten-column day schedule), where a 4-col grid just cramps vertically.
   */
  columns?: CardColumns;
  className?: string;
}

export function TableRow({
  children,
  variant = "card",
  columns = 4,
  className,
}: TableRowProps) {
  return (
    <ColumnsContext.Provider value={columns}>
      <tr
        className={cx(
          variant === "card"
            ? cx(styles.cardRow, CARD_COLUMNS[columns])
            : styles.row,
          className,
        )}
      >
        {children}
      </tr>
    </ColumnsContext.Provider>
  );
}

interface TableCellProps
  extends Omit<TdHTMLAttributes<HTMLTableCellElement>, "className"> {
  /**
   * Column name surfaced below the breakpoint via `data-label` + `::before`,
   * where the <thead> is hidden.
   *
   * The pseudo-element is presentational and is NOT reliably announced, so any
   * input inside the cell must carry its own `aria-label` (DESIGN.md
   * Accessibility). Omit `label` on cells whose content is self-describing
   * (an icon button, a spanning heading).
   */
  label?: string;
  /** Columns claimed in the mobile card grid. Clamped to the row's width. */
  span?: CardSpan;
  align?: "left" | "right";
  /**
   * Pulls the cell out of the card flow and into its top-right corner below
   * the breakpoint — for a row's delete/overflow control.
   */
  corner?: boolean;
  padding?: TablePadding;
  width?: TableWidth;
  /** Room for a sibling `corner` control, below the breakpoint only. */
  cornerGap?: TableCornerGap;
  className?: string;
}

export function TableCell({
  label,
  span = 2,
  align = "left",
  corner = false,
  padding = "default",
  width,
  cornerGap,
  className,
  children,
  ...rest
}: TableCellProps) {
  const columns = useContext(ColumnsContext);
  // A cell can't claim more of the card grid than the row actually has.
  const effectiveSpan = Math.min(span, columns) as CardSpan;
  return (
    <td
      data-label={label}
      className={cx(
        corner
          ? styles.corner
          : cx(styles.cell, CARD_SPANS[effectiveSpan]),
        PADDING[padding],
        width && WIDTH[width],
        cornerGap && CORNER_GAP[cornerGap],
        label && styles.labelled,
        align === "right" && styles.alignRight,
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

/**
 * A cell spanning the full table — group headings, subtotals, empty states.
 * Takes the real `colSpan` for the table presentation and the full card width
 * below the breakpoint.
 */
export function TableFullWidthCell({
  colSpan,
  padding = "default",
  className,
  children,
  ...rest
}: Omit<TdHTMLAttributes<HTMLTableCellElement>, "className"> & {
  colSpan: number;
  padding?: TablePadding;
  className?: string;
}) {
  const columns = useContext(ColumnsContext);
  return (
    <td
      colSpan={colSpan}
      className={cx(
        styles.cell,
        CARD_SPANS[columns],
        PADDING[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
